import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@web/components/ui/table';
import { UserDetailsDialog } from './user-details-dialog';
import { Pagination } from '@web/components/ui/pagination';
import { PaginationMeta, User } from '@shared/types';

// Define props for the UsersTable component
type UsersTableProps = {
  // Optional props for when the component is used directly
  onEditUser?: (userId: string) => void;
  users?: User[];
  pagination?: PaginationMeta & {
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
};

export function UsersTable({ users, pagination, onEditUser }: UsersTableProps) {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Handle user details dialog
  const handleUserEdit = (userId: string) => {
    if (onEditUser) {
      onEditUser(userId);
    } else {
      setSelectedUserId(userId);
    }
  };

  // Close dialog handler
  useEffect(() => {
    if (!selectedUserId) {
      // Any cleanup needed when dialog closes
    }
  }, [selectedUserId]);

  // Calculate pagination data
  const totalItems = pagination?.totalItems ?? 0;
  const pageSize = pagination?.itemsPerPage ?? 10;
  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <div className="space-y-4">
      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users?.map((user) => (
              <TableRow
                key={user.id}
                onClick={() => {
                  handleUserEdit(user.id);
                }}
                className="hover:bg-muted/50 cursor-pointer"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleUserEdit(user.id);
                  }
                }}
                aria-label={`View details for ${user.firstName} ${user.lastName ?? ''}`}
              >
                <TableCell className="font-medium">
                  {user.firstName} {user.lastName ?? ''}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {new Date(user.createdAt).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <Pagination
          currentPage={pagination?.currentPage ?? 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={
            pagination?.onPageChange ??
            (() => {
              /* no-op */
            })
          }
          onPageSizeChange={
            pagination?.onPageSizeChange ??
            (() => {
              /* no-op */
            })
          }
        />
      )}

      {/* User Details Dialog - only show if onEditUser is not provided */}
      {!onEditUser && selectedUserId && (
        <UserDetailsDialog
          userId={selectedUserId}
          open={!!selectedUserId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedUserId(null);
            }
          }}
        />
      )}
    </div>
  );
}
